// Shared fetch helpers for the AHRI mapping-review bulk tooling.
// Keeps URL building + auth headers + JSON handling in one place so the
// panel, filter bar, drawer, and rules manager all talk to the API the
// same way.

export function makeReviewApi(apiBaseUrl, getAuthHeaders) {
  const authHeaders = () => (typeof getAuthHeaders === 'function' ? getAuthHeaders() : {});

  const buildUrl = (path) => {
    try {
      const url = new URL(`${apiBaseUrl}${path}`, window.location.origin);
      return url.toString();
    } catch (_e) {
      return `${apiBaseUrl}${path}`;
    }
  };

  const request = async (path, { method = 'GET', body } = {}) => {
    const resp = await fetch(buildUrl(path), {
      method,
      headers: {
        ...authHeaders(),
        ...(body ? { 'Content-Type': 'application/json' } : {}),
      },
      body: body ? JSON.stringify(body) : undefined,
    });
    if (!resp.ok) {
      const text = await resp.text();
      throw new Error(text || `Request failed (${resp.status})`);
    }
    if (resp.status === 204) return null;
    return resp.json();
  };

  return {
    buildUrl,
    search: (predicate, limit, offset) =>
      request('/api/v1/ahri/mapping-review/search', {
        method: 'POST',
        body: { predicate, limit, offset },
      }),
    preview: (predicate) =>
      request('/api/v1/ahri/mapping-review/preview', {
        method: 'POST',
        body: { predicate },
      }),
    mismatchGroups: (maxAlignment) =>
      request('/api/v1/ahri/mapping-review/mismatch-groups', {
        method: 'POST',
        body: { max_alignment: maxAlignment },
      }),
    summary: () => request('/api/v1/ahri/mapping-review/summary'),
    facets: (hasCandidate = true) =>
      request(`/api/v1/ahri/mapping-review/facets?has_candidate=${hasCandidate ? 'true' : 'false'}`),
    startBulk: (action, predicate, expectedCount) =>
      request('/api/v1/ahri/mapping-review/bulk', {
        method: 'POST',
        body: { action, predicate, expected_count: expectedCount },
      }),
    bulkStatus: (jobId) =>
      request(`/api/v1/ahri/mapping-review/bulk/${encodeURIComponent(jobId)}`),
    // rules
    listRules: () => request('/api/v1/ahri/review-rules'),
    createRule: (rule) =>
      request('/api/v1/ahri/review-rules', { method: 'POST', body: rule }),
    updateRule: (id, patch) =>
      request(`/api/v1/ahri/review-rules/${id}`, { method: 'PATCH', body: patch }),
    deleteRule: (id) =>
      request(`/api/v1/ahri/review-rules/${id}`, { method: 'DELETE' }),
    simulateRule: (id) =>
      request(`/api/v1/ahri/review-rules/${id}/simulate`, { method: 'POST', body: {} }),
    applyRulesNow: () =>
      request('/api/v1/ahri/review-rules/apply', { method: 'POST', body: {} }),
    // aliases
    listAliases: () => request('/api/v1/ahri/manufacturer-aliases'),
    createAlias: (alias) =>
      request('/api/v1/ahri/manufacturer-aliases', { method: 'POST', body: alias }),
    deleteAlias: (id) =>
      request(`/api/v1/ahri/manufacturer-aliases/${id}`, { method: 'DELETE' }),
  };
}

// Score band presets keyed to the backend facet `score_buckets` keys.
export const SCORE_PRESETS = [
  { key: 'gte70', label: 'High (70+)', min: 70 },
  { key: 'b50_70', label: 'Medium (50–70)', min: 50, max: 70 },
  { key: 'b30_50', label: 'Partial (30–50)', min: 30, max: 50 },
  { key: 'lt30', label: 'Low (<30)', max: 30 },
];

// Manufacturer-alignment presets keyed to the facet `alignment_buckets` keys.
export const ALIGNMENT_PRESETS = [
  { key: 'mismatch', label: 'Mismatch (≤0.4)', max: 0.4 },
  { key: 'weak', label: 'Weak (0.4–0.7)', min: 0.4, max: 0.7 },
  { key: 'aligned', label: 'Aligned (>0.7)', min: 0.7 },
];

export const PART_STRENGTH_PRESETS = [
  { key: 'strong', label: 'Strong (≥0.8)', min: 0.8 },
  { key: 'medium', label: 'Medium (≥0.6)', min: 0.6 },
];

const byKey = (presets, key) => presets.find((p) => p.key === key) || null;

// Build a normalized predicate ({all:[...]}) from the structured filter state.
// Accepts the selection-based shape (arrays + preset keys) and still honors the
// legacy string keys used by the mismatch-group drill-in. Returns null when no
// conditions are set (caller treats that as "no filter / match all").
export function buildPredicate(filters) {
  const conds = [];
  const f = filters || {};
  const text = (v) => (v === undefined || v === null ? '' : String(v).trim());
  const num = (v) => (text(v) === '' ? null : Number(v));
  const arr = (v) => {
    if (Array.isArray(v)) return v.map((s) => String(s).trim()).filter(Boolean);
    return text(v)
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
  };
  // text in/eq helper for a multi-select dimension
  const pushIn = (field, values) => {
    if (values.length === 1) conds.push({ field, op: 'eq', value: values[0] });
    else if (values.length > 1) conds.push({ field, op: 'in', value: values });
  };

  if (text(f.ahriId)) conds.push({ field: 'ahri_id', op: 'contains', value: text(f.ahriId) });
  if (text(f.indoorModelNumber))
    conds.push({ field: 'indoor_model_number', op: 'contains', value: text(f.indoorModelNumber) });
  if (text(f.outdoorModelNumber))
    conds.push({ field: 'outdoor_model_number', op: 'contains', value: text(f.outdoorModelNumber) });
  if (text(f.furnaceModelNumber))
    conds.push({ field: 'furnace_model_number', op: 'contains', value: text(f.furnaceModelNumber) });

  pushIn('field', arr(f.fields !== undefined ? f.fields : f.field));
  pushIn('ahri_brand', arr(f.ahriBrands !== undefined ? f.ahriBrands : f.ahriBrand));
  pushIn(
    'candidate_manufacturer',
    arr(f.candidateManufacturers !== undefined ? f.candidateManufacturers : f.candidateManufacturer)
  );
  pushIn('program_name', arr(f.programs));
  pushIn('ahri_type', arr(f.ahriTypes));

  // Review reasons: OR-of-contains across the selected canonical reasons.
  const reasons = arr(f.reviewReasons);
  if (reasons.length === 1) {
    conds.push({ field: 'review_reasons', op: 'contains', value: reasons[0] });
  } else if (reasons.length > 1) {
    conds.push({ any: reasons.map((r) => ({ field: 'review_reasons', op: 'contains', value: r })) });
  } else if (text(f.reviewReasonContains)) {
    conds.push({ field: 'review_reasons', op: 'contains', value: text(f.reviewReasonContains) });
  }

  // Score band preset -> numeric range (or legacy min/max).
  const scoreP = byKey(SCORE_PRESETS, f.scoreBucket);
  if (scoreP) {
    if (scoreP.min !== undefined) conds.push({ field: 'score', op: 'gte', value: scoreP.min });
    if (scoreP.max !== undefined) conds.push({ field: 'score', op: 'lt', value: scoreP.max });
  } else {
    if (num(f.minScore) !== null) conds.push({ field: 'score', op: 'gte', value: num(f.minScore) });
    if (num(f.maxScore) !== null) conds.push({ field: 'score', op: 'lte', value: num(f.maxScore) });
  }

  // Manufacturer-alignment preset -> range (or legacy maxMfrAlignment).
  const alignP = byKey(ALIGNMENT_PRESETS, f.alignmentBucket);
  if (alignP) {
    if (alignP.min !== undefined)
      conds.push({ field: 'manufacturer_alignment', op: 'gt', value: alignP.min });
    if (alignP.max !== undefined)
      conds.push({ field: 'manufacturer_alignment', op: 'lte', value: alignP.max });
  } else if (num(f.maxMfrAlignment) !== null) {
    conds.push({ field: 'manufacturer_alignment', op: 'lte', value: num(f.maxMfrAlignment) });
  }

  // Part-match strength preset (or legacy minPartStrength).
  const strengthP = byKey(PART_STRENGTH_PRESETS, f.partStrengthPreset);
  if (strengthP) {
    conds.push({ field: 'part_match_strength', op: 'gte', value: strengthP.min });
  } else if (num(f.minPartStrength) !== null) {
    conds.push({ field: 'part_match_strength', op: 'gte', value: num(f.minPartStrength) });
  }

  if (text(f.partNumberRegex))
    conds.push({ field: 'candidate_part_number', op: 'regex', value: text(f.partNumberRegex) });
  if (text(f.sourceValueRegex))
    conds.push({ field: 'source_value', op: 'regex', value: text(f.sourceValueRegex) });
  if (f.needsReviewOnly) conds.push({ field: 'needs_review', op: 'is_true' });

  if (conds.length === 0) return null;
  return { all: conds };
}
